import { Component, Injector, OnInit, Input, ElementRef, EventEmitter, Output } from '@angular/core';
import { TextAutoCompleteComponent } from '../text-autocomplete/text-autocomplete.component';
import type { CoverpathBasePolicy } from '../../../../../classes/coverpath_subclasses/CoverpathBasePolicy';
import { Address, AddressHolder } from '../../../../../../common/js/customer/Address';
import { ApplicationService } from '../../../../services/application/application.service';

@Component({
  moduleId: module.id,
  selector: 'address-autocomplete',
  host: {
    '(document:click)': 'handleClick($event)',
  },
  templateUrl: './address-autocomplete.component.html',
})
export class TextAutoCompleteAddressComponent extends TextAutoCompleteComponent implements OnInit {
  @Output() setZip: EventEmitter<void> = new EventEmitter<void>();
  @Input() placeholder: string;
  @Input() insuredOrOwner: boolean;

  private _zipKept: boolean;
  @Input() set zipKept(value: boolean) {
    this._zipKept = value;
    if (this._zipKept) {
      const propPath = this.propertyName.substring(0, this.propertyName.lastIndexOf('.'));
      this.appService.submitValueAndFireTriggers(`${propPath}.street`, this.currentStreet);
    }
  }

  get zipKept(): boolean {
    return this._zipKept;
  }

  policy: CoverpathBasePolicy;
  addressOptions: Array<AddressHolder>;
  filteredAddressList: Array<AddressHolder> = [];
  currentStreet: string;
  line1: string;

  constructor(protected injector: Injector, protected appService: ApplicationService, myElement: ElementRef) {
    super(injector, appService, myElement);
    this.policy = this.appState.controller.customer.application.primaryPolicy as CoverpathBasePolicy;
  }
  ngOnInit() {
    super.ngOnInit();
    const propPath = this.propertyName.substring(0, this.propertyName.lastIndexOf('.'));
    this.currentStreet = this.getValue(`${propPath}.street`);
  }

  protected setOptions() {
    let values: Array<AddressHolder> = this.policy
      .getPolicyAddresses()
      .filter((item) => item.address && item.address.street);
    this.addressOptions = [];
    if (values && values.length) {
      values = values.filter((item) => this.prop.indexOf(item.prop) === -1);
      this.addressOptions = Array.from(
        new Set(values.map((item) => `${item.address.getLine1()}${item.address.getLine2()}`))
      ).map((addressString) =>
        values.find((item) => `${item.address.getLine1()}${item.address.getLine2()}` === addressString)
      );
    }
    this.addressOptions = this.addressOptions.sort();
  }

  filter() {
    let searchTerm = this.query;
    if (searchTerm !== '') {
      searchTerm = searchTerm.toLowerCase();
      this.filteredAddressList = this.addressOptions.filter((item) => {
        const searchItems = `${item.address.getLine1()}${item.address.getLine2()}`;
        const sameProp = this.prop.indexOf(item.prop) === 0;
        return !sameProp && searchItems.toLowerCase().indexOf(searchTerm) > -1;
      });
    } else {
      this.filteredAddressList = [...this.addressOptions];
    }
  }

  async select(item) {
    this.query = item;
    this.filteredAddressList = [];
    this.resetSelectedIndex();
    await this.validateAndSet(this.propertyName, this.query);
  }

  async trigger() {
    await this.validateAndSet(this.propertyName, this.query);
  }

  handleBlur() {
    if (!this.errorMessage[this.prop]) {
      this.checkEmptyAndValidate(this.propertyName, this.query);
    }
  }

  protected async validateAndSet(prop: string, value) {
    const propPath = prop.substring(0, prop.lastIndexOf('.'));

    const setStreetValues = () => {
      this.appService.submitValueAndFireTriggers(`${propPath}.street`, value.address.street);
      this.appService.submitValueAndFireTriggers(`${propPath}.line1`, value.address.line1);
      this.appService.submitValueAndFireTriggers(`${propPath}.line2`, value.address.line2);
      this.appService.submitValueAndFireTriggers(`${propPath}.line3`, value.address.line3);
    };

    if (value.address) {
      this.currentStreet = this.getValue(`${propPath}.street`);
      this.line1 = this.getValue(`${propPath}.line1`);
      if (this.getValue(`${propPath}.zip`) !== value.address.zip && !!value.address.zip) {
        this.setZip.emit(value.address.zip);
        if (this.zipKept) {
          this.appService.submitValueAndFireTriggers(`${propPath}.street`, this.currentStreet);
          this.appService.submitValueAndFireTriggers(`${propPath}.line1`, this.line1);
        } else {
          setStreetValues();
        }
      } else {
        setStreetValues();
        this.appService.submitValueAndFireTriggers(`${propPath}.zip`, value.address.zip);
        this.appService.submitValueAndFireTriggers(`${propPath}.city`, value.address.city);
        this.appService.submitValueAndFireTriggers(`${propPath}.state`, value.address.state);
      }
    } else {
      await super.validateAndSet(prop, value);
    }
    return true;
  }

  async handleKeyUpInteractions(keyCode) {
    if (keyCode === 13 && this.filteredAddressList.length) {
      // enter
      await this.select(this.filteredAddressList[this.selectedIndex]);
    } else if (keyCode === 38) {
      // up
      this.decrementSelectedIndex();
    } else if (keyCode === 40) {
      // down
      this.incrementSelectedIndex();
    } else {
      // any other key, the user is typing: reset the index and filter
      this.resetSelectedIndex();
      this.filter();
    }
  }

  handleKeyDownInteractions(keyCode) {
    if (keyCode === 9) {
      // tab
      this.filteredAddressList = [];
      this.resetSelectedIndex();
    }
  }

  handleClick(event) {
    let clickedComponent = event.target;
    let inside = false;

    do {
      if (clickedComponent === this.elementRef.nativeElement) {
        inside = true;
      }
      clickedComponent = clickedComponent.parentNode;
    } while (clickedComponent);

    if (!inside) {
      this.filteredAddressList = [];
    }
  }

  incrementSelectedIndex() {
    this.selectedIndex =
      this.selectedIndex < this.filteredAddressList.length - 1 ? this.selectedIndex + 1 : this.selectedIndex;
  }

  getFormattedAddress(address: Address): string {
    return `${address.getLine1()}${address.getLine2()}`;
  }

  updateFromModel() {
    this.setOptions();
    const value = this.getValue(this.propertyName) ? this.getValue(this.propertyName) + '' : '';
    if (value !== this.query) {
      this.query = value;
    }
  }
}
